import React, { useCallback, useEffect, useState } from "react";
import { FiEdit, FiPrinter } from "react-icons/fi";
import { useIntl } from "react-intl";
import { Checkbox, Form, notification, Spin } from "antd";
import { useSelector } from "react-redux";
import printJS from "print-js";

import {
  getCredentialEditForm,
  postCredentialEditReprint,
} from "../../../../service/credentials.service";
import { getEnabledEditableFields } from "../../../../constants/editableCredentialFields";
import { StyledFormItem } from "../../../../components/ModalNewCollaborator/styles";
import {
  ButtonFormConfirm,
  FooterContainer,
  HeaderModal,
  ModalSubTitle,
  ModalTitle,
  ModalWithdrawalAndDisassociate,
  StyledInput,
} from "../CredentialsPrintedTab/styles";
import { CredentialLabel, FieldsNote, LoadingWrapper } from "./styles";

const { TextArea } = StyledInput;

const REASON_MIN = 3;
const REASON_MAX = 500;

/**
 * Fluxo "Corrigir e reimprimir" a partir da aba de Retiradas (INFORMACP-4388).
 *
 * Usa os endpoints dedicados do backend:
 *   GET  /credential-reports/credential/{id}/edit-form    -> pré-preenche a fonte
 *   POST /credential-reports/credential/{id}/edit-reprint -> gera PDF + auditoria
 *
 * Edita os campos da whitelist (nome na credencial, empresa, cargo), exige um
 * motivo (auditoria) e permite promover nome/cargo à fonte (promoteToSource).
 * Imprime o PDF retornado e mantém o contexto (não navega para a lista de
 * colaboradores). A empresa nunca é promovida à fonte.
 */
export function QuickEditReprint({
  open,
  setOpen,
  selectedCredential, // { id, collaboratorName }
  fetchData,
}) {
  const intl = useIntl();
  const [form] = Form.useForm();
  const { token } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);

  const editableFields = getEnabledEditableFields();
  const credentialId = selectedCredential?.id;
  const eventId = localStorage.getItem("serviceEventId");
  const opportunityId = localStorage.getItem("serviceOpportunityId");

  const loadEditForm = useCallback(async () => {
    if (!credentialId) return;
    setLoadingForm(true);
    try {
      const response = await getCredentialEditForm({
        token,
        credentialId,
        eventId,
        opportunityId,
      });
      const formData = response.data?.form || {};
      form.setFieldsValue({
        credentialName: formData.credentialName ?? "",
        companyName: formData.companyName ?? "",
        jobTitle: formData.jobTitle ?? "",
        reason: "",
        promoteToSource: false,
      });
    } catch (error) {
      notification.error({
        message: intl.formatMessage({ id: "error" }),
        description:
          error.response?.data?.description ||
          intl.formatMessage({
            id: "api.error." + (error.response?.data?.code || "unknown"),
          }),
      });
    } finally {
      setLoadingForm(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentialId, token, eventId, opportunityId, intl]);

  useEffect(() => {
    if (open) loadEditForm();
  }, [open, loadEditForm]);

  const reset = useCallback(() => {
    setLoading(false);
    form.resetFields();
  }, [form]);

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const buildFeedback = (changedFields = [], promotedFields = []) => {
    const changed = changedFields
      .map((f) => {
        const field = editableFields.find((ef) => ef.apiKey === f);
        return field ? intl.formatMessage({ id: field.labelId }) : f;
      })
      .join(", ");
    let message = intl.formatMessage({ id: "edit_reprint_success" });
    if (changed) {
      message += ` ${intl.formatMessage(
        { id: "edit_reprint_changed_fields" },
        { fields: changed }
      )}`;
    }
    if (promotedFields.length > 0) {
      message += ` ${intl.formatMessage({ id: "edit_reprint_promoted" })}`;
    }
    return message;
  };

  const onConfirm = useCallback(async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // erros de validação já exibidos pelo Form
    }

    setLoading(true);
    try {
      const body = {
        reason: values.reason.trim(),
        promoteToSource: !!values.promoteToSource,
        eventId: Number(eventId),
        opportunityId: Number(opportunityId),
      };
      editableFields.forEach((field) => {
        const value = (values[field.key] ?? "").trim();
        if (value) body[field.apiKey] = value;
      });

      const response = await postCredentialEditReprint({
        token,
        credentialId,
        body,
      });

      const data = response.data || {};
      const pdf = data.reportOutput?.file;
      if (pdf) {
        printJS({ printable: pdf, type: "pdf", base64: true });
      }

      notification.success({
        description: buildFeedback(data.changedFields, data.promotedFields),
      });

      setOpen(false);
      reset();
      fetchData();
    } catch (error) {
      const code = error.response?.data?.code;
      const description = error.response?.data?.description;

      if (code === -15042) {
        notification.info({
          description: intl.formatMessage({ id: "edit_reprint_no_changes" }),
        });
      } else {
        notification.error({
          message: intl.formatMessage({ id: "error" }),
          description:
            description ||
            intl.formatMessage({ id: "api.error." + (code || "unknown") }),
        });
      }
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form,
    token,
    credentialId,
    eventId,
    opportunityId,
    intl,
    setOpen,
    reset,
    fetchData,
  ]);

  return (
    <ModalWithdrawalAndDisassociate
      width={658}
      height="auto"
      closable={false}
      onCancel={handleClose}
      visible={open}
      footer=""
      destroyOnClose
      centered
    >
      <div>
        <HeaderModal>
          <div className="icon">
            <FiEdit
              style={{ fontSize: "18px", color: "rgba(6, 142, 195, 1)" }}
            />
          </div>
          <ModalTitle style={{ textTransform: "none" }}>
            {intl.formatMessage({ id: "edit_and_reprint_credential" })}
          </ModalTitle>
        </HeaderModal>

        <CredentialLabel>
          {intl.formatMessage({ id: "collaborator_name" })}:{" "}
          <strong>{selectedCredential?.collaboratorName}</strong>
        </CredentialLabel>

        <ModalSubTitle>
          {intl.formatMessage({ id: "edit_and_reprint_instructions" })}
        </ModalSubTitle>

        {loadingForm ? (
          <LoadingWrapper>
            <Spin />
          </LoadingWrapper>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={onConfirm}
            style={{ marginTop: "16px" }}
          >
            {editableFields.map((field) => (
              <StyledFormItem
                key={field.key}
                name={field.key}
                label={intl.formatMessage({ id: field.labelId })}
              >
                <StyledInput
                  placeholder={intl.formatMessage({ id: field.labelId })}
                />
              </StyledFormItem>
            ))}

            <StyledFormItem
              name="reason"
              label={intl.formatMessage({ id: "reason" })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: "error.field_required" }),
                },
                {
                  min: REASON_MIN,
                  max: REASON_MAX,
                  message: intl.formatMessage(
                    { id: "edit_reprint_reason_length" },
                    { min: REASON_MIN, max: REASON_MAX }
                  ),
                },
              ]}
            >
              <TextArea rows={3} maxLength={REASON_MAX} />
            </StyledFormItem>

            <StyledFormItem name="promoteToSource" valuePropName="checked">
              <Checkbox>
                {intl.formatMessage({ id: "edit_reprint_promote_to_source" })}
              </Checkbox>
            </StyledFormItem>

            <FieldsNote>
              {intl.formatMessage({ id: "edit_reprint_promote_note" })}
            </FieldsNote>
          </Form>
        )}

        <FooterContainer>
          <div />
          <div style={{ marginRight: "10px" }}>
            <ButtonFormConfirm onClick={handleClose} style={{ border: "none" }}>
              {intl.formatMessage({ id: "cancel" })}
            </ButtonFormConfirm>
            <ButtonFormConfirm
              loading={loading}
              disabled={loadingForm}
              onClick={() => form.submit()}
              style={{
                backgroundColor: "rgba(6, 142, 195, 1)",
                color: "rgba(255, 255, 255, 1)",
              }}
            >
              <FiPrinter style={{ marginRight: 6 }} />
              {intl.formatMessage({ id: "save_and_reprint" })}
            </ButtonFormConfirm>
          </div>
        </FooterContainer>
      </div>
    </ModalWithdrawalAndDisassociate>
  );
}

export default QuickEditReprint;
